import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import {
  SUPPORTED_LANGUAGES,
  buildLocalizedPath,
  stripLangPrefix,
  LANGUAGE_STORAGE_KEY,
} from "../constants/languages";

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const currentLang = SUPPORTED_LANGUAGES.find((lang) => lang.code === i18n.language);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const handleSwitch = (langCode) => {
    setIsOpen(false);
    if (langCode === i18n.language) return;
    const barePath = stripLangPrefix(location.pathname);
    const target = buildLocalizedPath(barePath, langCode);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, langCode);
    i18n.changeLanguage(langCode);
    navigate(`${target}${location.search}`);
  };

  return (
    <div className="relative" ref={containerRef} data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        data-testid="language-switcher-trigger"
        className="flex items-center gap-1 text-xs uppercase tracking-wider text-white/70 hover:text-gold transition-colors"
      >
        <span>{currentLang?.label}</span>
        <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div
          role="listbox"
          className="absolute top-full end-0 mt-2 min-w-[140px] bg-white rounded-sm shadow-lg overflow-hidden z-50"
        >
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              role="option"
              aria-selected={i18n.language === lang.code}
              onClick={() => handleSwitch(lang.code)}
              data-testid={`language-switch-${lang.code}`}
              className={`block w-full text-start px-4 py-2 text-sm transition-colors ${
                i18n.language === lang.code
                  ? "text-gold font-semibold bg-slate-50"
                  : "text-navy hover:text-gold hover:bg-slate-50"
              }`}
            >
              {lang.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
